import { useCallback, useEffect, useRef, useState } from 'react';
// @mui
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { StepIconProps } from '@mui/material/StepIcon';
import Stepper from '@mui/material/Stepper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
//
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import Iconify from 'src/components/iconify';
import { Colors } from 'src/theme/palette';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { SplashScreen } from 'src/components/loading-screen';
import { IftaFiling, MilesSource } from 'src/models';
//
import { WIZARD_STEPS, activeStep as deriveStep } from 'src/sections/filing-wizard/wizard-steps';
import StepPeriod from 'src/sections/filing-wizard/step-period';
import StepMilesSource from 'src/sections/filing-wizard/step-miles-source';
import StepMilesUpload from 'src/sections/filing-wizard/step-miles-upload';
import StepFuelUpload from 'src/sections/filing-wizard/step-fuel-upload';
import StepReview from 'src/sections/filing-wizard/step-review';

// ----------------------------------------------------------------------

// Custom step icon: a flat circle, no borders or backing rings. Completed and
// active are lime with dark content; upcoming are dark with white numbers.
function WizardStepIcon({ active, completed, icon }: StepIconProps) {
  const lime = active || completed;
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: lime ? Colors.brandPrimary : '#39423C',
        color: lime ? Colors.greyText : '#FFFFFF',
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {completed ? <Iconify icon="eva:checkmark-fill" width={16} sx={{ color: Colors.greyText }} /> : icon}
    </Box>
  );
}

export default function FilingWizardPage() {
  const params = useParams();
  const router = useRouter();
  const filingId = params.id as string;

  const [filing, setFiling] = useState<IftaFiling | null>(null);
  const [canImportCt, setCanImportCt] = useState(false);
  const [saving, setSaving] = useState(false);
  // Holds the wizard on the current step after an upload so the user can see
  // the preview before moving on; null = derive from the filing record.
  const [heldStep, setHeldStep] = useState<number | null>(null);
  // On desktop the "Fixing issue" card replaces the header + stepper instead of
  // floating over them; StepReview reports when one is pinned.
  const pinnedHostRef = useRef<HTMLDivElement>(null);
  const [pinnedActive, setPinnedActive] = useState(false);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const [discarding, setDiscarding] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const load = useCallback(async () => {
    const response = await axios.get(formatEndpoint(endpoints.filings.single, { id: filingId }));
    setFiling(response.data.filing);
    setCanImportCt(response.data.canImportCt);
  }, [filingId]);

  useEffect(() => {
    load().catch(() => router.replace(paths.filings.root));
  }, [load, router]);

  useEffect(() => {
    if (filing?.paymentStatus === 'paid') {
      router.replace(paths.filings.report(filing.id));
    }
  }, [filing, router]);

  if (!filing) {
    return <SplashScreen />;
  }

  const currentStep = heldStep !== null ? heldStep : deriveStep(filing);

  const discardFiling = async () => {
    setDiscarding(true);
    try {
      await axios.delete(formatEndpoint(endpoints.filings.destroy, { id: filing.id }));
      enqueueSnackbar('Filing discarded', { variant: 'info' });
      router.replace(paths.filings.current);
    } finally {
      setDiscarding(false);
      setDiscardDialogOpen(false);
    }
  };

  const onBack =
    currentStep > 0 && filing.paymentStatus !== 'paid'
      ? () => setHeldStep(currentStep - 1)
      : undefined;


  const updateSettings = async (payload: Partial<{ selectedQuarter: string; milesSource: MilesSource }>) => {
    setSaving(true);
    try {
      const response = await axios.put(
        formatEndpoint(endpoints.filings.update, { id: filing.id }),
        payload
      );
      setFiling(response.data);
      setHeldStep(null);
    } finally {
      setSaving(false);
    }
  };

  const headerReplaced = pinnedActive && mdUp;

  return (
    <Stack spacing={4}>
      {!headerReplaced && (
      <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h4">{filing.title}</Typography>
          {filing.selectedQuarter && (
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {filing.selectedQuarter}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="soft"
            color="error"
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" width={16} />}
            onClick={() => setDiscardDialogOpen(true)}
            sx={{ border: 'none', '&:hover': { border: 'none' } }}
            data-testid="discard-filing"
          >
            Discard
          </Button>
        </Stack>
      </Stack>

      <Stepper
        activeStep={currentStep}
        alternativeLabel
        sx={{
          overflowX: 'auto',
          pb: { xs: 1, sm: 0 },
          '& .MuiStep-root': { minWidth: { xs: 96, sm: 0 } },
          '& .MuiStepLabel-label': {
            '&.Mui-active, &.Mui-completed': { color: 'text.primary' },
          },
        }}
      >
        {WIZARD_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={WizardStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      </>
      )}

      <Box ref={pinnedHostRef} sx={{ display: headerReplaced ? 'block' : 'none' }} />

      <Card sx={{ p: { xs: 2, md: 4 } }}>
        {currentStep === 0 && (
          <StepPeriod
            initialQuarter={filing.selectedQuarter}
            saving={saving}
            onSave={(selectedQuarter) => updateSettings({ selectedQuarter })}
            onBack={onBack}
          />
        )}
        {currentStep === 1 && (
          <StepMilesSource
            initialSource={filing.milesSource}
            saving={saving}
            onSave={(milesSource) => updateSettings({ milesSource })}
            onBack={onBack}
          />
        )}
        {currentStep === 2 && (
          <StepMilesUpload
            filing={filing}
            canImportCt={canImportCt}
            onUploaded={(updated) => {
              setFiling(updated);
              setHeldStep(2);
            }}
            onContinue={() => setHeldStep(null)}
            onBack={onBack}
            onSkip={() => setHeldStep(3)}
          />
        )}
        {currentStep === 3 && (
          <StepFuelUpload
            filing={filing}
            onUploaded={(updated) => {
              setFiling(updated);
              setHeldStep(3);
            }}
            onContinue={() => setHeldStep(null)}
            onBack={onBack}
            onSkip={() => setHeldStep(4)}
          />
        )}
        {currentStep === 4 && (
          <StepReview
            filing={filing}
            onCalculated={setFiling}
            onBack={onBack}
            pinnedHost={pinnedHostRef}
            onPinnedActiveChange={setPinnedActive}
          />
        )}
      </Card>

      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard this filing?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This permanently deletes the filing for {filing.selectedQuarter || 'this period'},
            including all uploaded miles and fuel data. This action can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDiscardDialogOpen(false)} data-testid="cancel-discard">
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={discarding}
            onClick={discardFiling}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" width={16} />}
            data-testid="confirm-discard"
          >
            Discard filing
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
